import axios from "axios";
import { GENERAL_URL_API } from "../urls";

const FILAS_HOME_URL = `${GENERAL_URL_API}/filasHome`;
const EDITAR_FILAS_URL = `${FILAS_HOME_URL}/editarFilasHomeRecomendados`;

/**
 * Normaliza el valor del campo 'activo' al formato que espera el backend.
 * Acepta booleanos, números o strings ("1"/"0"). Devuelve "1" o "0".
 */
const normalizeActivo = (activo) => {
  if (activo === true || activo === 1 || activo === "1") return "1";
  return "0";
};

/** Fila marcada como estática en BD / API: no debe reordenarse en el admin. */
export const isEstaticoTrue = (val) =>
  val === true || val === 1 || val === "1" || val === "true";

const isActivoTrue = (val) =>
  val === true || val === 1 || val === "1" || val === "true";

/** Filas activas ordenadas por posición absoluta (`orden`). */
export const getFilasActivasOrdenadas = (filas = []) =>
  filas
    .filter((f) => isActivoTrue(f.activo))
    .sort((a, b) => compareFilasPorOrden(a, b));

const compareFilasPorOrden = (a, b) => {
  const oa =
    a.orden !== null && a.orden !== undefined && a.orden !== ""
      ? Number(a.orden)
      : 999999;
  const ob =
    b.orden !== null && b.orden !== undefined && b.orden !== ""
      ? Number(b.orden)
      : 999999;
  if (oa === ob) {
    return String(a.filaHomeName ?? "").localeCompare(
      String(b.filaHomeName ?? "")
    );
  }
  return oa - ob;
};

/** Orden de listado admin: activas por `orden`, inactivas al final. */
export const sortFilasParaListado = (filas = []) =>
  [...filas].sort((a, b) => {
    const aActive = isActivoTrue(a.activo);
    const bActive = isActivoTrue(b.activo);
    if (aActive !== bActive) return aActive ? -1 : 1;
    return compareFilasPorOrden(a, b);
  });

/**
 * Índice con el que intercambiar al subir/bajar, omitiendo filas fijas intermedias
 * (p. ej. posición 5 reservada para colecciones).
 * @returns {number} índice destino o -1 si no hay movimiento posible
 */
export const getIndiceSwapReorden = (filasActivas, idxActual, direccion) => {
  if (!Array.isArray(filasActivas) || idxActual < 0) return -1;

  let idx = idxActual + direccion;
  while (
    idx >= 0 &&
    idx < filasActivas.length &&
    isEstaticoTrue(filasActivas[idx]?.estatico)
  ) {
    idx += direccion;
  }

  if (idx < 0 || idx >= filasActivas.length) return -1;
  return idx;
};

export async function fetchFilasHome() {
  const { data } = await axios.get(`${FILAS_HOME_URL}/verFilasHome`, {
    withCredentials: true,
  });

  return data?.data?.filasHome ?? [];
}

/**
 * Obtiene los libros de una fila específica.
 * @param {string} filaHome - El nombre de la fila de la que se obtendrán los libros.
 * @returns {Promise<Array>} - Una promesa que se resuelve en un arreglo de libros obtenidos de la fila.
 */
export async function fetchLibrosFila(filaHome) {
  const fd = new FormData();
  fd.append("filaHome", filaHome);

  const { data } = await axios.post(`${FILAS_HOME_URL}/verLibrosFila`, fd, {
    withCredentials: true,
  });

  return data?.data ?? [];
}

/**
 * Crea una nueva fila home.
 * @param {Object} options
 * @param {string} options.nombre
 * @param {string} options.descripcion
 * @param {string} options.disponibilidadRegional - lista separada por comas
 * @param {string} [options.filaHomeNameEng]
 * @param {string} [options.descriptionEng]
 * @param {boolean|string|number} [options.activo] - estado por defecto al crear
 */
export async function createFilaHome({
  nombre,
  descripcion,
  disponibilidadRegional,
  filaHomeNameEng = "",
  descriptionEng = "",
  activo = false,
}) {
  const fd = new FormData();
  fd.append("editionType", "añadirFilaHome");
  fd.append("filaHome", nombre);
  fd.append("description", descripcion);
  fd.append("disponibilidadRegional", disponibilidadRegional);
  fd.append("filaHomeNameEng", filaHomeNameEng);
  fd.append("descriptionEng", descriptionEng);
  fd.append("activo", normalizeActivo(activo));

  const { data } = await axios.post(EDITAR_FILAS_URL, fd, {
    withCredentials: true,
  });

  return data;
}

/**
 * Edita una fila home existente.
 * @param {Object} options
 * @param {string} options.nombre - Nombre actual o nuevo de la fila
 * @param {string} [options.nombreOriginal] - Nombre original al editar (para renombrar)
 * @param {string} options.descripcion
 * @param {string} options.disponibilidadRegional
 * @param {string} [options.filaHomeNameEng]
 * @param {string} [options.descriptionEng]
 * @param {boolean|string|number} [options.activo]
 */
export async function editFilaHome({
  nombre,
  nombreOriginal,
  descripcion,
  disponibilidadRegional,
  filaHomeNameEng = "",
  descriptionEng = "",
  activo,
}) {
  const nombreOriginalFinal = (nombreOriginal ?? nombre)?.trim();
  const nombreNuevo = nombre?.trim();

  const fd = new FormData();
  fd.append("editionType", "editarFilaHome");
  fd.append("filaHome", nombreOriginalFinal);
  fd.append("filaHomeNameNuevo", nombreNuevo);
  fd.append("description", descripcion);
  fd.append("disponibilidadRegional", disponibilidadRegional);
  fd.append("filaHomeNameEng", filaHomeNameEng);
  fd.append("descriptionEng", descriptionEng);
  if (activo !== undefined) {
    fd.append("activo", normalizeActivo(activo));
  }

  const { data } = await axios.post(EDITAR_FILAS_URL, fd, {
    withCredentials: true,
  });

  return data;
}

/**
 * Cambia únicamente el estado activo/inactivo de una fila.
 * Atajo para usar desde la tabla sin reenviar todos los datos.
 */
export async function toggleFilaHomeActivo({ nombre, activo, fila = {} }) {
  return editFilaHome({
    nombre,
    nombreOriginal: fila?.filaHomeName ?? nombre,
    descripcion: fila?.description ?? "",
    disponibilidadRegional: fila?.disponibilidadRegional ?? "",
    filaHomeNameEng: fila?.filaHomeNameEng ?? "",
    descriptionEng: fila?.descriptionEng ?? "",
    activo,
  });
}

/**
 * Elimina una fila home existente según su nombre.
 */
export async function deleteFilaHome(nombre) {
  const fd = new FormData();
  fd.append("editionType", "eliminarFilaHome");
  fd.append("filaHome", nombre);

  const { data } = await axios.post(EDITAR_FILAS_URL, fd, {
    withCredentials: true,
  });

  return data;
}

/**
 * Agrega un libro a una fila home específica.
 */
export async function agregarLibroFila({ idLibro, nombreFila }) {
  const fd = new FormData();
  fd.append("editionType", "añadirLibro");
  fd.append("idLibro", idLibro);
  fd.append("filaHome", nombreFila);

  const { data } = await axios.post(EDITAR_FILAS_URL, fd, {
    withCredentials: true,
  });

  return data;
}

/**
 * Elimina un libro de una fila home específica.
 */
export async function eliminarLibroFila({ idLibro, nombreFila }) {
  const fd = new FormData();
  fd.append("editionType", "eliminarLibro");
  fd.append("idLibro", idLibro);
  fd.append("filaHome", nombreFila);

  const { data } = await axios.post(EDITAR_FILAS_URL, fd, {
    withCredentials: true,
  });

  return data;
}

/**
 * Guarda el nuevo orden de las filas home activas dinámicas.
 *
 * Cada entrada debe incluir el orden absoluto de la fila en el home
 * (p. ej. 2, 4, 6). Las filas fijas (1, 3, 5) no se envían ni se modifican.
 *
 * @param {Object} options
 * @param {Array<{filaHome: string, orden: number}>} options.filasOrdenadas
 */
export async function setOrdenFilaHome({ filasOrdenadas }) {
  const fd = new FormData();
  fd.append("editionType", "reordenarFilas");
  fd.append("orden", JSON.stringify(filasOrdenadas));

  const { data } = await axios.post(EDITAR_FILAS_URL, fd, {
    withCredentials: true,
  });

  return data;
}

export async function fetchPaquetesLibro(idLibro) {
  const fd = new FormData();
  fd.append("idLibro", idLibro);

  const { data } = await axios.post(
    `${GENERAL_URL_API}/paquetes/verPaquetesLibro`,
    fd,
    {
      withCredentials: true,
    }
  );

  return data?.data ?? [];
}
